/**
 * Utility functions for text and HTML content processing
 */

/**
 * Checks if HTML content is effectively empty (no text content after stripping HTML tags)
 * @param html - The HTML string to check
 * @returns True if the HTML contains no text content, false otherwise
 */
export const isHtmlContentEmpty = (html: string): boolean => {
  if (!html || !html.trim()) return true;
  // Strip HTML tags and check if there's any text content
  const textContent = html.replace(/<[^>]*>/g, '').trim();
  return !textContent || textContent === '';
};

/**
 * Removes leading and trailing <br> tags from HTML content and trims whitespace
 * @param html - The HTML string to clean
 * @returns The cleaned HTML string with leading/trailing <br> tags removed
 */
export const removeLeadingTrailingBrTags = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/^<br\s*\/?>/i, '') // Remove leading <br> tags
    .replace(/^<br\s*\/?>\s*/gi, '') // Remove leading <br> tags with whitespace
    .replace(/<br\s*\/?>\s*$/i, '') // Remove trailing <br> tags
    .replace(/\s*<br\s*\/?>\s*$/gi, '') // Remove trailing <br> tags with whitespace
    .trim();
};

/**
 * Sanitizes rich-text HTML by keeping only explicitly allowed tags and stripping all attributes.
 * Disallowed elements are unwrapped (their text/children are kept).
 */
export const sanitizeRichTextAllowlist = (
  html: string,
  allowedTags: string[] = ['b', 'i', 'em', 'strong', 'u', 'sub', 'sup']
): string => {
  if (!html) return '';

  // Runtime-safe fallback for non-DOM environments.
  if (typeof DOMParser === 'undefined') {
    return html.replace(/<(?!(\/?(b|i|em|strong|u|sub|sup)\b))[^>]*>/gi, '').trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return '';

  const allowed = new Set(allowedTags.map((t) => t.toLowerCase()));

  const sanitizeNode = (node: Node): void => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      sanitizeNode(child);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'div') return;

    if (!allowed.has(tag)) {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
      return;
    }

    // Drop all attributes on allowed tags.
    for (const attr of Array.from(el.attributes)) {
      el.removeAttribute(attr.name);
    }
  };

  sanitizeNode(root);
  return root.innerHTML.trim();
};
